import React, { useState } from "react";
import { X, Upload, FileText } from "lucide-react";
import api from "../../lib/api.ts";
// currency utilities
import { useCurrencyUtils } from "../../utils/currencyUtils";

const SellerInvoicePaymentPopup = ({
  isOpen,
  onClose,
  selectedItem,
  itemType = "vehicle", // "vehicle" or "invoice"
  onPaymentSuccess,
}) => {
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [paymentType, setPaymentType] = useState("full"); // 'full' or 'partial'
  const [customAmount, setCustomAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);

  const { convertPrice, displayPriceWithSymbol } = useCurrencyUtils();

  // Reset state when popup opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setReceiptFile(null);
      setPaymentType("full");
      setCustomAmount("");
      setAmountError("");
      setUploadError("");
      setUploadProgress(0);
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Clear previous errors
      setUploadError("");

      // Check file size (increased to 10MB for better user experience)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        setUploadError(
          `File size (${(file.size / (1024 * 1024)).toFixed(
            1
          )}MB) exceeds the maximum allowed size of 10MB. Please compress the file or choose a smaller one.`
        );
        return;
      }

      // Check file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        setUploadError(
          `File type "${file.type}" is not supported. Please upload a PDF, JPG, or PNG file.`
        );
        return;
      }

      setReceiptFile(file);
    }
  };

  const handleSubmitPayment = async () => {
    if (!receiptFile) {
      setUploadError("Please select a payment receipt file");
      return;
    }

    // Clear previous errors
    setUploadError("");

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append("imageUrl", receiptFile);
      formData.append("sellerInvoiceId", selectedItem.id);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      const response = await api.post(
        `/sellerinvoicepayments/create`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 60000, // 60 second timeout
        }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Close popup and call success callback
      setTimeout(() => {
        onClose();
        if (onPaymentSuccess) {
          onPaymentSuccess();
        }
      }, 500);
    } catch (error) {
      console.error("Error uploading payment receipt:", error);

      // Detailed error handling
      let errorMessage = "Failed to upload payment receipt. ";

      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;

        if (status === 413) {
          errorMessage +=
            "File is too large. Please compress the file or choose a smaller one.";
        } else if (status === 415) {
          errorMessage +=
            "File type not supported. Please upload a PDF, JPG, or PNG file.";
        } else if (status === 400) {
          errorMessage +=
            data?.message ||
            "Invalid request. Please check your payment details.";
        } else if (status === 500) {
          errorMessage += "Server error. Please try again later.";
        } else {
          errorMessage +=
            data?.message || `Server error (${status}). Please try again.`;
        }
      } else if (error.request) {
        // Network error
        errorMessage +=
          "Network error. Please check your internet connection and try again.";
      } else if (error.code === "ECONNABORTED") {
        // Timeout error
        errorMessage +=
          "Upload timeout. The file might be too large or your connection is slow. Please try again.";
      } else {
        // Other errors
        errorMessage += "Unexpected error occurred. Please try again.";
      }

      setUploadError(errorMessage);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (!isOpen || !selectedItem) return null;

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
      <div className='bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto'>
        {/* Header */}
        <div className='flex items-center justify-between p-6 border-b'>
          <h3 className='text-lg font-semibold text-gray-900'>
            Upload Payment Receipt
          </h3>
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 transition-colors'
            disabled={uploading}
          >
            <X className='w-6 h-6' />
          </button>
        </div>

        {/* Content */}
        <div className='p-6'>
          {/* Item Details */}
          <div className='mb-6'>
            <h4 className='font-medium text-gray-900 mb-2'>
              {itemType === "vehicle" ? "Vehicle Details" : "Invoice Details"}
            </h4>
            <div className='bg-gray-50 p-4 rounded-lg'>
              <p className='text-sm text-gray-600 mb-1'>
                <span className='font-medium'>Invoice Number:</span>{" "}
                {selectedItem.id}
              </p>
              <p className='text-sm text-gray-600 mb-1'>
                <span className='font-medium'>Amount:</span>{" "}
                {displayPriceWithSymbol(convertPrice(selectedItem.totalAmount))}
              </p>
            </div>
          </div>

          {/* File Upload */}
          <div className='mb-6'>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Payment Receipt <span className='text-red-500'>*</span>
            </label>
            <div className='border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors'>
              <input
                type='file'
                id='receipt-upload'
                accept='.pdf,.jpg,.jpeg,.png'
                onChange={handleFileChange}
                className='hidden'
                disabled={uploading}
              />
              <label
                htmlFor='receipt-upload'
                className='cursor-pointer flex flex-col items-center'
              >
                <Upload className='w-8 h-8 text-gray-400 mb-2' />
                <span className='text-sm text-gray-600 mb-1'>
                  Click to upload payment receipt
                </span>
                <span className='text-xs text-gray-500'>
                  PDF, JPG, PNG up to 10MB
                </span>
              </label>
            </div>

            {receiptFile && (
              <div className='mt-3 flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3'>
                <div className='flex items-center'>
                  <FileText className='w-5 h-5 text-green-600 mr-2' />
                  <div>
                    <span className='text-sm text-green-800 font-medium'>
                      {receiptFile.name}
                    </span>
                    <div className='text-xs text-green-600'>
                      {(receiptFile.size / (1024 * 1024)).toFixed(1)}MB
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setReceiptFile(null)}
                  className='text-green-600 hover:text-green-800'
                  disabled={uploading}
                >
                  <X className='w-4 h-4' />
                </button>
              </div>
            )}

            {/* Upload Progress */}
            {uploading && (
              <div className='mt-3'>
                <div className='flex items-center justify-between text-sm text-gray-600 mb-1'>
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className='w-full bg-gray-200 rounded-full h-2'>
                  <div
                    className='bg-orange-500 h-2 rounded-full transition-all duration-300'
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {uploadError && (
              <div className='mt-3 bg-red-50 border border-red-200 rounded-lg p-3'>
                <div className='flex items-start'>
                  {/* <XCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" /> */}
                  <div>
                    <h4 className='text-sm font-medium text-red-800 mb-1'>
                      Upload Failed
                    </h4>
                    <p className='text-sm text-red-700'>{uploadError}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className='mb-6'>
            <h4 className='font-medium text-gray-900 mb-2'>Instructions</h4>
            <ul className='text-sm text-gray-600 space-y-1'>
              <li>• Upload a clear photo or scan of your payment receipt</li>
              <li>• Ensure the receipt shows the payment amount and date</li>
              <li>• File should be in PDF, JPG, or PNG format</li>
              <li>• Maximum file size is 10MB</li>
              <li>• For large files, consider compressing the image</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className='flex space-x-3'>
            <button
              onClick={onClose}
              className='flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-orange-200 transition-colors'
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmitPayment}
              disabled={
                !receiptFile ||
                uploading ||
                uploadError ||
                (paymentType === "partial" && (!customAmount || amountError))
              }
              className='flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {uploading ? "Uploading..." : "Submit Payment"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerInvoicePaymentPopup;
